import React, { useState, useMemo } from 'react'
import startCase from 'lodash/startCase'
import moment from 'moment'
import { useSelector } from 'react-redux'
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast
} from '@chakra-ui/react'
import { TrashIcon } from '@heroicons/react/20/solid'
import { capabilities, documents, rentals, rentalUsers } from '@homevest/utils'

import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { UploadDocumentPanel } from 'components/Documents/UploadDocumentPanel'
import { LINK_COLOR } from 'constants/colors'
import { useRentRollDocumentsDataQuery, useUpupDeleteDocumentMutation } from 'graphql/generated'
import { hasCapability } from 'lib/admin-perms'
import axios from 'lib/axios'
import { StoreState } from 'store'
import { Sidepeek } from 'ui'

const { CAPABILITY_TYPES } = capabilities
const { DOCUMENT_TYPES, DELETABLE_DOCUMENT_TYPES } = documents
const { RENTAL_STATUS } = rentals
const { RENTAL_USER_ROLES } = rentalUsers

type RentalDocument = {
  id: string
  friendly_name: string
  created_at?: string | null
  type: string
  uploaded_by_admin?: {
    first_name: string
    last_name: string
  } | null
}
type DocumentsRental = {
  id: string
  status: string
  rental_users?: Array<{
    role: string
    user?: {
      first_name?: string | null
      last_name?: string | null
    } | null
  }>
  agreements?: Array<{ documents: RentalDocument[] }>
  rental_application?: {
    document_agreements?: Array<{ documents: RentalDocument[] }>
  } | null
  documents?: RentalDocument[]
}

export const DocumentsCard = ({
  rental,
  refreshDocuments
}: {
  rental: DocumentsRental
  refreshDocuments?: () => void
}) => {
  const admin = useSelector((state: StoreState) => state.admin)
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const toast = useToast()

  const documentList = useMemo(() => {
    const directDocuments = rental.documents ?? []
    const rentalApplicationDocuments =
      rental.rental_application?.document_agreements?.[0]?.documents ?? []

    const rentalAgreementDocuments = (rental.agreements || []).flatMap(
      (agreement) => agreement.documents ?? []
    )

    const documents = [
      ...directDocuments,
      ...rentalApplicationDocuments,
      ...rentalAgreementDocuments
    ]

    return documents.reduce((acc, doc) => {
      if (!acc.find((d) => d.id === doc.id)) {
        acc.push(doc)
      }
      return acc
    }, [] as RentalDocument[])
  }, [rental])

  const [{ fetching: isDeleting }, deleteDocument] = useUpupDeleteDocumentMutation()

  const tenant = rental.rental_users?.find((ru) => ru.role === RENTAL_USER_ROLES.PRIMARY)?.user
  const tenantName = tenant ? `${tenant.first_name} ${tenant.last_name}` : undefined
  const isRentalComplete = [RENTAL_STATUS.COMPLETE, RENTAL_STATUS.CANCELED].includes(
    rental.status as any
  )

  const uploadButton = (
    <Button size='xs' colorScheme='teal' onClick={() => setDrawerOpen(true)}>
      + Document
      <Sidepeek isOpen={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <UploadDocumentPanel
          fixedStatus={isRentalComplete ? 'final' : undefined}
          generateName={
            isRentalComplete ? (type) => (tenantName ? `${type} - ${tenantName}` : type) : undefined
          }
          setClosed={() => {
            setDrawerOpen(false)
            toast({
              title: 'Document uploaded',
              status: 'success',
              duration: 3000,
              isClosable: true,
              position: 'top'
            })
          }}
          onUpload={async () => {
            refreshDocuments?.()
          }}
          resourceId={rental.id}
          resourceType='rentals'
          typeMap={{
            [DOCUMENT_TYPES.APPLICATION_PACKET]: 'Application Packet',
            [DOCUMENT_TYPES.ASSIGNMENT_OF_LEASE_AND_PURCHASE_OPTION_AGREEMENT]:
              'Assignment of Lease and POA',
            [DOCUMENT_TYPES.CONSENT_TO_ASSIGNMENT_OF_LEASE_AND_PURCHASE_OPTION_AGREEMENT]:
              'Consent to Assignment of Lease and POA',
            [DOCUMENT_TYPES.EVICTION_LEGAL_DOCUMENT]: 'Eviction Legal Document',
            [DOCUMENT_TYPES.LEASE]: 'Lease',
            [DOCUMENT_TYPES.LEASE_AMENDMENT]: 'Lease Amendment',
            [DOCUMENT_TYPES.LEASE_TERMINATION]: 'Lease Termination',
            [DOCUMENT_TYPES.MOVE_IN_STATEMENT]: 'Move In Statement',
            [DOCUMENT_TYPES.MOVE_OUT_RESIDENT_CHARGEBACKS]: 'Move Out Resident Chargebacks',
            [DOCUMENT_TYPES.MOVE_OUT_RESIDENT_STATEMENT]: 'Move Out Resident Statement',
            [DOCUMENT_TYPES.OTHER]: 'Other',
            [DOCUMENT_TYPES.PURCHASE_OPTION_AGREEMENT_MEMORANDUM]: 'POA Memorandum',
            [DOCUMENT_TYPES.RENTER_INSURANCE]: "Renter's Insurance",
            [DOCUMENT_TYPES.VOIDED_CHECK]: 'Voided Check',
            [DOCUMENT_TYPES.W9]: 'W9'
          }}
        />
      </Sidepeek>
    </Button>
  )

  const canDeleteDocuments = hasCapability(admin, CAPABILITY_TYPES.DOCUMENT_DELETER)

  const deleteButton = (documentId: string) => (
    <Menu placement='start'>
      <Tooltip label='Delete'>
        <MenuButton as={Button} size='sm' variant='outline'>
          <TrashIcon height={20} />
        </MenuButton>
      </Tooltip>
      <MenuList minW={0} p={1}>
        <MenuItem
          as={Button}
          size='sm'
          color='white'
          backgroundColor='red'
          isLoading={isDeleting}
          isDisabled={isDeleting}
          onClick={async (e) => {
            e.stopPropagation()
            const res = await deleteDocument({ documentId })
            if (!res.error) {
              refreshDocuments?.()
            }
          }}
        >
          Delete
        </MenuItem>
      </MenuList>
    </Menu>
  )

  return (
    <ContentSectionCard collapsable={false} title={'Documents'} action={uploadButton}>
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Uploaded</Th>
              <Th>Uploaded By</Th>
              <Th>Type</Th>
              {canDeleteDocuments && <Th></Th>}
            </Tr>
          </Thead>
          <Tbody>
            {documentList?.map((doc) => (
              <Tr key={doc.id}>
                <Td>
                  <Button
                    variant='link'
                    color={LINK_COLOR}
                    onClick={async () => {
                      const url = await axios
                        .get(`/admin/documents/${doc.id}/url`)
                        .then((resp) => resp.data.url)
                      if (url) {
                        window.open(url, '_blank')
                      } else {
                        toast({
                          title: 'Failed to fetch document URL',
                          status: 'error',
                          duration: 3000,
                          isClosable: true,
                          position: 'top-right'
                        })
                      }
                    }}
                  >
                    {doc.friendly_name}
                  </Button>
                </Td>
                <Td>{doc.created_at ? moment(doc.created_at).format('YYYY-MM-DD') : 'N/A'}</Td>
                <Td>
                  {doc.uploaded_by_admin
                    ? `${doc.uploaded_by_admin?.first_name} ${doc.uploaded_by_admin?.last_name}`
                    : 'N/A'}
                </Td>
                <Td>{startCase(doc.type)}</Td>
                {canDeleteDocuments && DELETABLE_DOCUMENT_TYPES.includes(doc.type as any) && (
                  <Td>{deleteButton(doc.id)}</Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </ContentSectionCard>
  )
}

const isDocumentsRentalReady = (
  rental: DocumentsRental | null | undefined
): rental is DocumentsRental =>
  Boolean(
    rental &&
      Array.isArray(rental.rental_users) &&
      Array.isArray(rental.agreements) &&
      Array.isArray(rental.documents) &&
      (!rental.rental_application || Array.isArray(rental.rental_application.document_agreements))
  )

const Documents = ({ rentalId }: { rentalId: string }) => {
  const queryContext = useMemo(() => ({ suspense: false }), [])
  const [{ data, fetching, error }, refetchDocuments] = useRentRollDocumentsDataQuery({
    variables: { rentalId },
    requestPolicy: 'cache-and-network',
    context: queryContext
  })
  const rental = data?.rentals_by_pk

  const refreshDocuments = () => {
    refetchDocuments({ requestPolicy: 'network-only' })
  }

  const isRentalReady = isDocumentsRentalReady(rental)

  if (fetching && !isRentalReady) {
    return (
      <ContentSectionCard collapsable={false} title={'Documents'}>
        <Text color='gray.500'>Loading documents...</Text>
      </ContentSectionCard>
    )
  }

  if (error && !isRentalReady) {
    return (
      <ContentSectionCard collapsable={false} title={'Documents'}>
        <Text color='red.600'>Unable to load documents.</Text>
      </ContentSectionCard>
    )
  }

  if (!rental) {
    return null
  }

  if (!isRentalReady) {
    return (
      <ContentSectionCard collapsable={false} title={'Documents'}>
        <Text color='gray.500'>Loading documents...</Text>
      </ContentSectionCard>
    )
  }

  return <DocumentsCard rental={rental} refreshDocuments={refreshDocuments} />
}

export default Documents
